// src/components/RelazioniCommessaWidget.js
// "Relazioni della Commessa" — mappa automatica di tutto ciò che è già collegato alla
// commessa, usando solo dati/collegamenti già esistenti in Nexiora. Nessun dato nuovo,
// nessuna compilazione richiesta: solo conteggi (getDocs) sulle collezioni già in uso,
// con link diretto alla sezione corrispondente. Progettato per essere facilmente
// espandibile: basta aggiungere una voce all'array MODULI per far comparire un nuovo
// collegamento in futuro.
import React, { useState, useEffect } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useVedeTutteFatturazioni } from "../hooks/useCanSee";
import { useNav } from "../contexts/NavContext";

// Ogni modulo: collezione da contare, campo di collegamento alla commessa, etichetta, icona.
// Due modi per aprire un modulo al clic:
//  - tabId: scheda interna della stessa commessa (cambia semplicemente la tab attiva)
//  - pagina: modulo che vive fuori dalla commessa, in un Registro a parte — si apre quella
//    pagina con il filtro "Commessa" già precompilato su questa commessa (deep link)
const MODULI = [
  { key:"richiesteCommerciali", label:"Richieste Commerciali", icon:"💼", col:"richiesteCommerciali", campoOrg:"orgId", campo:"projectId", tabId:"richieste-commerciali" },
  { key:"ddt",                  label:"DDT",                   icon:"🚚", col:"ddt", campoOrg:"orgId", campo:"projectId", tabId:"ddt" },
  { key:"comunicazioni",        label:"Comunicazioni",         icon:"✉️", col:"comunicazioni", campoOrg:"orgId", campo:"projectId", tabId:"comunicazioni" },
  { key:"verbali",              label:"Verbali",                icon:"📄", col:"verbali", campoOrg:"orgId", campo:"projectId", tabId:"verbali" },
  { key:"sopralluoghi",         label:"Sopralluoghi",           icon:"🔍", col:"sopralluoghi", campoOrg:"orgId", campo:"projectId", tabId:"sopralluogo" },
  { key:"rapportini",           label:"Rapporti di Intervento", icon:"📋", col:"rapportini", campoOrg:"orgId", campo:"projectId", tabId:"rapportini" },
  { key:"tasks",                label:"Attività",               icon:"✅", col:"tasks", campoOrg:"organizationId",                campo:"projectId", tabId:"list" },
  { key:"cronoprogrammaFasi",   label:"Cronoprogramma",         icon:"📅", col:"cronoprogrammaFasi", campoOrg:"orgId",   campo:"projectId", tabId:"cronoprogramma" },
  { key:"giornaleCantiereDays", label:"Giornale di Cantiere",   icon:"📓", col:"giornaleCantiereDays", campoOrg:"orgId", campo:"projectId", tabId:"giornale" },
  { key:"nuoveForniture",       label:"Richieste NF",           icon:"📦", col:"nuoveForniture", campoOrg:"organizationId",       campo:"commessaId", tabId:"richiedi-nf" },
  { key:"richiesteMateriali",   label:"Richieste al Magazzino", icon:"🧰", col:"richiesteMateriali", campoOrg:"organizationId",   campo:"projectId", tabId:"materiali" },
  { key:"richiesteFatturazione",label:"Richieste Fatturazione", icon:"💶", col:"richiesteFatturazione", campoOrg:"organizationId",campo:"projectId", tabId:null, pagina:"da-fatturare" },
  { key:"reportCommessa",       label:"Report Commessa",        icon:"📊", col:"reportCommessa", campoOrg:"orgId",       campo:"projectId", tabId:"report" },
  { key:"segnalazioniCliente",  label:"Segnalazioni Cliente",   icon:"📣", col:"segnalazioniCliente", campoOrg:"orgId",  campo:"projectId", tabId:null, pagina:"registro-segnalazioni" },
  { key:"rientriMateriali",     label:"Rientro Materiali",      icon:"🔄", col:"rientriMateriali", campoOrg:"organizationId",     campo:"projectId", tabId:null, pagina:"registro-rientri-materiali" },
];

export default function RelazioniCommessaWidget({ projectId, orgId, clienteId, clienteNome, responsabileUid, responsabileNome, onNavigateTab }) {
  const [conteggi, setConteggi] = useState(null); // null = ancora in caricamento
  const [aperto, setAperto] = useState(true);
  const [pmDipId, setPmDipId] = useState(null); // id del documento dipendenti collegato al PM, per il deep-link
  const { navigateFull } = useNav();

  // Trova il documento "dipendenti" del PM (per il collegamento alla Scheda Dipendente) —
  // dato già esistente (dipendenti.nexoraUid), nessuna nuova informazione richiesta all'utente.
  useEffect(() => {
    if (!responsabileUid || !orgId) { setPmDipId(null); return; }
    getDocs(query(collection(db,"dipendenti"), where("organizationId","==",orgId), where("nexoraUid","==",responsabileUid)))
      .then(snap => setPmDipId(snap.docs[0]?.id || null))
      .catch(() => setPmDipId(null));
  }, [responsabileUid, orgId]);

  // Le Regole leggono per azienda: ogni conteggio filtra anche per organizzazione (senza,
  // la lettura veniva rifiutata e il modulo risultava vuoto). Le richieste di fatturazione:
  // tutte per chi le gestisce, le proprie per il PM (regole 57).
  const { currentUser } = useAuth();
  const vedeTutteFatt = useVedeTutteFatturazioni();
  useEffect(() => {
    if (!projectId || !orgId || vedeTutteFatt === null) return;
    let annullato = false;
    Promise.all(
      MODULI.map(m => {
        // W421: ogni conteggio è protetto anche da errori nel costruire la richiesta (prima un
        // modulo senza campo azienda faceva cadere tutta la vista Dashboard della commessa)
        try {
          return getDocs(query(collection(db, m.col), where(m.campo, "==", projectId), where(m.campoOrg, "==", orgId),
            ...(m.col === "richiesteFatturazione" && !vedeTutteFatt ? [where("richiedenteUid", "==", currentUser?.uid || "")] : [])))
            .then(snap => [m.key, snap.size])
            .catch(() => [m.key, 0]); // collezione non ancora usata/regola mancante → tratta come "assente", non blocca il resto
        } catch (e) {
          console.warn("Relazioni della commessa, conteggio non riuscito:", m.key, e?.message);
          return Promise.resolve([m.key, 0]);
        }
      })
    ).then(coppie => {
      if (annullato) return;
      const mappa = {};
      coppie.forEach(([k, n]) => { mappa[k] = n; });
      setConteggi(mappa);
    });
    return () => { annullato = true; };
  }, [projectId, orgId, vedeTutteFatt, currentUser?.uid]);

  const vai = (m) => {
    if (m.tabId && onNavigateTab) onNavigateTab(m.tabId);
    else if (m.pagina) navigateFull({ page: m.pagina, projectId });
  };

  return (
    <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:12, marginBottom:20, overflow:"hidden" }}>
      <div onClick={()=>setAperto(a=>!a)} style={{ padding:"14px 18px", display:"flex", alignItems:"center", gap:8, cursor:"pointer", background:"#faf5ff", borderBottom: aperto ? "1px solid #e9d5ff" : "none" }}>
        <span style={{ fontSize:16 }}>🔗</span>
        <h3 style={{ fontSize:14, fontWeight:700, color:"#1e1b4b", margin:0 }}>Relazioni della Commessa</h3>
        <span style={{ fontSize:11, color:"#9ca3af", fontWeight:400 }}>· tutto ciò che è già collegato, a colpo d'occhio</span>
        <span style={{ marginLeft:"auto", fontSize:12, color:"#7c3aed" }}>{aperto ? "▲" : "▼"}</span>
      </div>

      {aperto && (
        <div style={{ padding:16 }}>
          {/* Cliente e PM — sempre noti dalla commessa stessa, nessuna query necessaria */}
          <div style={{ display:"flex", gap:10, marginBottom:14, flexWrap:"wrap" }}>
            <div onClick={()=>clienteId && navigateFull({page:"clienti", clienteId})}
              style={{ flex:1, minWidth:160, background: clienteNome ? "#f0fdf4" : "#f9fafb", borderRadius:8, padding:"9px 12px", cursor: clienteId?"pointer":"default" }}>
              <div style={{ fontSize:10, color:"#9ca3af", fontWeight:700 }}>👤 CLIENTE</div>
              <div style={{ fontSize:13, fontWeight:600, color: clienteNome ? "#065f46" : "#9ca3af" }}>{clienteNome ? `🟢 ${clienteNome}` : "⚪ Senza cliente"}</div>
            </div>
            <div onClick={()=>pmDipId && navigateFull({page:"dipendenti", dipId:pmDipId})}
              style={{ flex:1, minWidth:160, background: responsabileNome ? "#f0fdf4" : "#f9fafb", borderRadius:8, padding:"9px 12px", cursor: pmDipId?"pointer":"default" }}>
              <div style={{ fontSize:10, color:"#9ca3af", fontWeight:700 }}>🧑‍💼 PM / RESPONSABILE</div>
              <div style={{ fontSize:13, fontWeight:600, color: responsabileNome ? "#065f46" : "#9ca3af" }}>{responsabileNome ? `🟢 ${responsabileNome}` : "⚪ Non assegnato"}</div>
            </div>
          </div>

          {conteggi === null ? (
            <div style={{ fontSize:12, color:"#9ca3af", textAlign:"center", padding:12 }}>Verifico i collegamenti…</div>
          ) : (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:8 }}>
              {MODULI.map(m => {
                const n = conteggi[m.key] || 0;
                const presente = n > 0;
                const cliccabile = presente && (m.tabId || m.pagina);
                return (
                  <div key={m.key} onClick={()=>cliccabile && vai(m)}
                    style={{ display:"flex", alignItems:"center", gap:8, padding:"9px 10px", borderRadius:8,
                      background: presente ? "#f0fdf4" : "#f9fafb", border:`1px solid ${presente?"#bbf7d0":"#e5e7eb"}`,
                      cursor: cliccabile ? "pointer" : "default" }}>
                    <span style={{ fontSize:15 }}>{m.icon}</span>
                    <div style={{ minWidth:0, flex:1 }}>
                      <div style={{ fontSize:11.5, fontWeight:600, color: presente ? "#065f46" : "#9ca3af", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {presente ? "🟢" : "⚪"} {m.label}
                      </div>
                      {presente && <div style={{ fontSize:10, color:"#059669" }}>{n} element{n===1?"o":"i"}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ fontSize:10, color:"#9ca3af", marginTop:12, fontStyle:"italic" }}>
            Aggiornato in automatico — nessuna compilazione richiesta. Clic su una voce presente per aprirla direttamente.
          </div>
        </div>
      )}
    </div>
  );
}
